import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  quotationDetailsListColumns,
  quotationListColumns,
  ViewQuotationListColumns,
} from "../../../helpers/adminConstants/TableConstants";
import { ViewQuotationWrapper } from "./styled";
import {
  BackButton,
  Box,
  ButtonStyledIconLeft,
  HeadingStyled,
  SpanStyled,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import Breadcrumbs from "../../../components/admin/Breadcrumbs/Breadcrumbs";
import DynamicTableList from "../../../components/common/tables/DynamicTableList";
import { toast } from "react-toastify";
import QuotationsApi from "../../../services/api/quotationsApi";
import { useUser } from "../../../context/userContext";
import ListTable from "../../../components/common/tables/ListTable";
import {
  EditIcon,
  Invoice,
  LogoAr,
  LogoEn,
} from "../../../components/styled/AllImages";
import DeleteModal from "../../../components/admin/Modals/DeleteModal/DeleteModal";
import { generateInvoicePDF, generateQuotationPDF } from "../../../utils/generationPdf";
import { toBase64 } from "../../../utils";

const ViewQuotation = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { user } = useUser();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rowsData, setRowsData] = useState([]);
  const [quotationDetails, setQuotationDetails] = useState(null);
  const [quotationId, setQuotationId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [logoBase64, setLogoBase64] = useState(null);

  const searchParams = new URLSearchParams(location.search);
  const childQuotation = searchParams.get("child");

  const onDelete = (row) => {
    setQuotationId(row?.id);
    setShowDeleteModal(true);
  };

  const handleView = (quotation) => {
    navigate(`/quotations/${quotation?.id}/view-quotation?child=${true}`);
  };

  const handleParentInvoiceDownload = async (row) => {
    const data = {
      ...quotationDetails,
      invoice_no: quotationDetails?.quotation_no,
    };
    await generateInvoicePDF(data, logoBase64, "sales_invoice.pdf");
  };

  useEffect(() => {
    if (user?.organization?.logo) {
      toBase64(
        `${process.env.REACT_APP_ASSET_URL}/${user?.organization?.logo}`
      ).then(setLogoBase64);
    } else {
      setLogoBase64(language === "en" ? LogoEn : LogoAr);
    }
  }, []);

  const handleChildInvoiceDownload = async (row) => {
    const data = await fetchQuotationDetails(row?.id);
    const formatedData = {
      ...data,
      invoice_no: data?.quotation_no,
    };
    await generateInvoicePDF(formatedData, logoBase64, "sales_invoice.pdf");
  };
  const handleChildQuotationDownload = async (row) => {
    const data = await fetchQuotationDetails(row?.id);
    await generateQuotationPDF(data, logoBase64, "quotation.pdf");
  };

  const fetchQuotationDetails = async (id) => {
    try {
      const { data, code } = await QuotationsApi.viewQuotation(id);
      if (code === 200 && data) {
        return data;
      }
    } catch (error) {
      toast.error(error?.message);
      return null;
    } finally {
    }
  };

  const handleDelete = async () => {
    try {
      setLoadingDelete(true);
      const response = await QuotationsApi.deleteQuotation(quotationId);
      if (response.code === 200) {
        toast.success(t("quotation_deleted_successfully"));
        setShowDeleteModal(false);
        viewQuotationDetails();
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
      if (response.code === 400) {
        toast.error(response?.error);
      }
    } catch (error) {
    } finally {
      setLoadingDelete(false);
    }
  };

  const viewQuotationDetails = async () => {
    try {
      setLoading(true);
      const { data, code } = await QuotationsApi.viewQuotation(id);
      if (code === 200 && data) {
        const { products, client_name_en, client_name_ar } = data;

        setRowsData(
          products.map((item, index) => ({
            no: index + 1,
            category:
              language === "en"
                ? item?.category_name_en
                : item?.category_name_ar,
            product:
              language === "en" ? item.product_name_en : item.product_name_ar,
            quantity: Number(item.quantity),
            unit_price: `${Number(item.unit_price)} ${
              user?.organization?.currency?.stringCode || t("sar")
            }`,
            unit: item.unit,
            discount: `${Number(item.discount)} ${
              user?.organization?.currency?.stringCode || t("sar")
            }`,
            total: `${Number(item.total)} ${
              user?.organization?.currency?.stringCode || t("sar")
            }`,
            net_total: `${Number(item.net_total)} ${
              user?.organization?.currency?.stringCode || t("sar")
            }`,
            vat: `${Number(item.vat)} ${
              user?.organization?.currency?.stringCode || t("sar")
            }`,
            subtotal: `${Number(item.subtotal)} ${
              user?.organization?.currency?.stringCode || t("sar")
            }`,
          }))
        );
        setQuotationDetails({
          ...data,
          client_name: language === "en" ? client_name_en : client_name_ar,
        });
      }
    } catch (error) {
      toast.error(error?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    viewQuotationDetails();
  }, [id]);

  return (
    <ViewQuotationWrapper>
      <>
        <Box className="content-head">
          <Box className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-3">
            <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-4">
              <BackButton
                label={t("quotation_details")}
                onClick={() =>
                  navigate(
                    childQuotation && quotationDetails?.parent_id
                      ? `/quotations/${quotationDetails?.parent_id}/view-quotation`
                      : `/quotations`
                  )
                }
              />
              <Breadcrumbs />
            </Box>
            {!childQuotation && (
              <Box className="d-flex align-items-center gap-2 flex-sm-nowrap flex-wrap">
                <ButtonStyledIconLeft
                  label={t("generate_invoice")}
                  color="var(--white)"
                  bg="var(--themeColor)"
                  icon={Invoice}
                  onClick={handleParentInvoiceDownload}
                />
                <ButtonStyledIconLeft
                  label={t("edit_quotation")}
                  icon={EditIcon}
                  color="var(--white)"
                  onClick={() => navigate(`/quotations/${id}/edit-quotation`)}
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box className="table-wrapper">
          {loading ? (
            <ThemeLoader />
          ) : (
            <>
              <Box>
                <SpanStyled color="var(--text2)">
                  {t("quotation_to")}
                </SpanStyled>
                <SubHeadingStyled color="var(--text)" size="20px" weight="600">
                  {quotationDetails?.client_name}
                </SubHeadingStyled>
                <Box className="d-flex flex-column mt-3 info-wrapper">
                  <Box className="d-flex gap-3 align-items-center justify-content-between sub-title-wrapper">
                    <SpanStyled color="var(--text2)">
                      {t("quotation_id")}
                    </SpanStyled>
                    <SubHeadingStyled
                      color="var(--text)"
                      size="14px"
                      weight="500"
                    >
                      {quotationDetails?.quotation_no}
                    </SubHeadingStyled>
                  </Box>
                  <hr
                    style={{
                      color: "var(--text2)",
                      backgroundColor: "var(--text2)",
                    }}
                  />
                  <Box className="d-flex align-items-center justify-content-between sub-title-wrapper">
                    <SpanStyled color="var(--text2)">
                      {t("issue_date")}
                    </SpanStyled>
                    <SubHeadingStyled
                      color="var(--text)"
                      size="14px"
                      weight="500"
                    >
                      {quotationDetails?.date}
                    </SubHeadingStyled>
                  </Box>
                </Box>
              </Box>
              <Box>
                <DynamicTableList
                  fields={rowsData}
                  cols={ViewQuotationListColumns}
                  readOnly={true}
                />
              </Box>
              <Box>
                <Box className="footer-wrapper">
                  {[
                    { label: "subtotal", value: quotationDetails?.sub_total },
                    { label: "discount", value: quotationDetails?.discount },
                    {
                      label: "total_after_discount",
                      value: quotationDetails?.amount_after_discount,
                    },
                    {
                      label: "taxable_amount",
                      value: quotationDetails?.tax_value,
                    },
                    {
                      label: "total_amount",
                      value: quotationDetails?.total_amount,
                    },
                  ]?.map((item, idx) => (
                    <Box key={idx}>
                      <SpanStyled color="var(--text2)" size="14px">
                        {t(item?.label)}
                      </SpanStyled>
                      <SubHeadingStyled
                        color="var(--text)"
                        size="14px"
                        weight="500"
                      >
                        {item?.value} &nbsp;
                        {user?.organization?.currency?.stringCode || t("sar")}
                      </SubHeadingStyled>
                    </Box>
                  ))}
                </Box>
                {quotationDetails?.notes && (
                  <Box className="">
                    <SpanStyled color="var(--text2)" size="14px">
                      {t("note")}
                    </SpanStyled>
                    <SubHeadingStyled
                      color="var(--text)"
                      size="14px"
                      weight="500"
                    >
                      {quotationDetails?.notes}
                    </SubHeadingStyled>
                  </Box>
                )}
              </Box>
              {!childQuotation && (
                <Box
                  style={{ backgroundColor: "var(--bgPrimary)" }}
                  className="rounded-4 p-3"
                >
                  <Box>
                    <HeadingStyled color="var(--text)">
                      {t("quotation_versions")}
                    </HeadingStyled>
                  </Box>
                  <ListTable
                    options={["invoice", "view", "download", "delete"]}
                    rows={quotationDetails?.child_quotations}
                    columns={quotationDetailsListColumns}
                    onDelete={onDelete}
                    onOpen={handleView}
                    onDownload={handleChildQuotationDownload}
                    onInvoiceDownload={handleChildInvoiceDownload}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </>

      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        title={t("quotation")}
        isLoading={loadingDelete}
      />
    </ViewQuotationWrapper>
  );
};

export default ViewQuotation;
